<script lang="ts" setup>
	import { useQueryClient } from '@tanstack/vue-query'
	import { computed, onMounted, onUnmounted, ref, toRefs } from 'vue'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import StyledInput from '@/components/StyledInput.vue'
	import { serviceDescriptor } from '@/config/services'

	import useDateFormat from '@/composables/useDateFormat'

	import type { Cef } from '@/modules/trips/interfaces/cef'

	import useEditCef from '../composables/useCef'
	import { useCefRequests } from '../composables/useCefsRequests'
	import { baseFareOf, isQuotingFare } from '../utils/tripFormatters'
	import ListRequest from './ListRequest.vue'

	interface Props {
		cef: Cef
		isFetching?: boolean
		activeTab?: string
	}

	interface Emits {
		(e: 'refetch'): void
		(e: 'closePreviewRequest'): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()

	const isEdit = ref(false)
	const toast = useToast()
	const name = ref('')
	const scrollY = ref(0)

	const { cef } = toRefs(props)
	const queryClient = useQueryClient()

	// Función para manejar el scroll con throttling mejorado
	let scrollTimeout: number | null = null
	const updateScrollPosition = () => {
		if (scrollTimeout) return

		scrollTimeout = window.requestAnimationFrame(() => {
			scrollY.value = window.scrollY
			scrollTimeout = null
		})
	}

	// Lifecycle hooks para el scroll
	onMounted(() => {
		window.addEventListener('scroll', updateScrollPosition)
		updateScrollPosition() // Inicializar posición
	})

	onUnmounted(() => {
		window.removeEventListener('scroll', updateScrollPosition)
		if (scrollTimeout) {
			window.cancelAnimationFrame(scrollTimeout)
		}
	})

	const { requests, refetch, isFetching: requestsLoading } = useCefRequests({
		params: {
			cefId: props.cef.id,
		},
		immediate: true,
	})

	const serviceCount = computed(() =>
		requestsLoading.value
			? cef.value.usedServices.reduce((acc, s) => acc + s.providers.length, 0)
			: requests.value.length
	)

	// Los iconos salen del registry (`src/config/services.ts`).
	const serviceIcon = (serviceName: string | null | undefined) =>
		serviceDescriptor(serviceName).icon

	const { mutationCefAsync } = useEditCef()

	const getModernStatus = (state: string) => {
		const statusMap: Record<
			string,
			{ classes: string; message: string; dotColor: string }
		> = {
			confirmado: {
				classes:
					'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200/50',
				message: 'Confirmado',
				dotColor: 'bg-emerald-500',
			},
			creado: {
				classes:
					'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200/50',
				message: 'Pendiente',
				dotColor: 'bg-amber-500',
			},
			cancelado: {
				classes:
					'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50',
				message: 'Cancelado',
				dotColor: 'bg-red-500',
			},
			// Oculto a pedido del cliente (First Cash) junto con el resto de
			// facturación: se muestra como "Confirmado" en vez de exponer el
			// estado de facturado mientras esa sección está pausada.
			// Mismo reetiquetado en RecentBookingsWidget.vue (CONFIRMED_STATES) —
			// al reactivar facturación, revertir los dos, ninguno tiene bandera.
			facturado: {
				classes:
					'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200/50',
				message: 'Confirmado',
				dotColor: 'bg-emerald-500',
			},
			pagado: {
				classes:
					'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200/50',
				message: 'Pagado',
				dotColor: 'bg-emerald-500',
			},
			procesada: {
				classes:
					'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200/50',
				message: 'Confirmado',
				dotColor: 'bg-emerald-500',
			},
		}

		const normalizedState = (state || '').toLowerCase().trim()
		return (
			statusMap[normalizedState] || {
				classes:
					'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/50',
				message: state || 'En proceso',
				dotColor: 'bg-gray-500',
			}
		)
	}

	const editRequest = () => {
		isEdit.value = !isEdit.value
	}

	const editCef = async () => {
		try {
			await mutationCefAsync({
				cefId: props.cef.id,
				name: name.value,
			})

			toast.success('Itinerario actualizado correctamente')

			// await emits('refetch')
			await queryClient.refetchQueries(['cefs'])
			await queryClient.invalidateQueries(['cefs'])

			editRequest()
		} catch (error: any) {
			toast.error(error.response.data.message)
		}
	}

	const formatRates = (rate: number) => {
		const formatter = new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
		})

		return `${formatter.format(rate)} MXN`
	}

	// `request.total` es un accessor del backend que da null para vuelos
	// (Request::total en BookingV2 solo cubre Hotel/Car/Bus); sumarlo cruzado
	// da 0 falso para itinerarios con vuelo. Se recalcula aquí con clientFare +
	// currencyConversion, que sí cubre los 4 servicios.
	const formatRate = computed(() =>
		requests.value.reduce(
			(acc, request) => acc + baseFareOf(request.clientFare, request.currencyConversion),
			0
		)
	)

	// "Cotizando" solo si de verdad hay algo sin cotizar (creado) entre los
	// requests que suman al total en 0 — un itinerario cancelado sin tarifa no
	// está cotizando nada, ya terminó.
	const isQuoting = computed(
		() =>
			formatRate.value === 0 &&
			requests.value.some((r) => isQuotingFare(r.stateId, r.clientFare))
	)

	const router = useRouter()
</script>

<template>
	<div class="relative bg-white rounded-xl border border-gray-200 w-full overflow-hidden shadow-sm">
		<!-- Header simplificado -->
		<div class="relative bg-gray-50 border-b border-gray-200 p-4">
			<!-- Glass effect overlay -->

			<div class="flex justify-between items-center">
				<!-- Close button simplificado -->
				<button
					@click="() => emits('closePreviewRequest')"
					class="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						></path>
					</svg>
				</button>

				<!-- Title simplificado -->
				<h2
					class="text-gray-900 font-semibold text-lg flex items-center justify-center flex-1"
				>
					Detalle del Itinerario
				</h2>
			</div>
		</div>

		<!-- Contenido principal simplificado -->
		<div class="p-4 space-y-4">
			<!-- Información del itinerario -->
			<div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
				<div
					class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
				>
					<div class="flex-1 space-y-2">
						<div>
							<div
								v-if="!isEdit"
								class="flex items-center gap-2 cursor-pointer group/edit"
								@click="editRequest"
							>
								<h1 class="font-semibold text-lg text-gray-900">
									{{ name || cef.name }}
								</h1>
								<svg
									class="w-4 h-4 text-gray-400 opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
									></path>
								</svg>
							</div>

							<form
								v-else
								class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
								@submit.prevent="editCef"
							>
								<div class="relative flex-1">
									<StyledInput
										:model-value="name"
										@update:model-value="
											(value: string) => {
												name = value
											}
										"
										:value="cef.name"
										input-class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
									/>
								</div>
								<button
									type="submit"
									class="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors duration-200"
								>
									Guardar
								</button>
							</form>
						</div>

						<div
							class="flex flex-col sm:flex-row sm:items-center sm:justify-start"
						>
							<div class="flex items-center gap-2">
								<div class="sm:hidden">
									<span
										:class="[
											getModernStatus(cef.state).classes,
											'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
										]"
									>
										{{ getModernStatus(cef.state).message }}
									</span>
								</div>
							</div>
							<div
								class="flex items-center justify-center gap-1 text-xs text-gray-500"
							>
								<svg
									class="w-3 h-3"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									></path>
								</svg>
								<span class="font-medium capitalize">{{
									cef.activityPeriod
								}}</span>
							</div>
						</div>
					</div>

					<div class="hidden sm:block">
						<span
							:class="[
								getModernStatus(cef.state).classes,
								'inline-flex items-center px-3 py-1 rounded text-xs font-medium',
							]"
						>
							{{ getModernStatus(cef.state).message }}
						</span>
					</div>
				</div>
			</div>

			<!-- Información del viajero -->
			<div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
				<div
					class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
				>
					<div class="flex items-center gap-3">
						<img
							:src="requests[0]?.traveler?.avatar"
							class="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
							:alt="cef?.travelerName"
						/>
						<div>
							<div class="flex items-center gap-2 mb-1">
								<svg
									class="w-3 h-3 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									></path>
								</svg>
								<span class="text-xs font-medium text-gray-600"
									>Viajero Principal</span
								>
							</div>
							<h3 class="font-semibold text-gray-900">
								{{ cef?.travelerName }}
							</h3>
						</div>
					</div>

					<div class="text-left sm:text-right">
						<div
							class="flex items-center gap-1 justify-start sm:justify-end mb-1"
						>
							<span class="text-xs font-medium text-gray-600"
								>Total del Itinerario</span
							>
						</div>
						<p
							class="font-bold"
							:class="[
								requestsLoading ? 'animate-pulse' : '',
								isQuoting ? 'text-sm text-amber-600' : 'text-xl text-green-600',
							]"
						>
							{{ requestsLoading ? '•••' : isQuoting ? 'Cotizando…' : formatRates(formatRate) }}
						</p>
						<div
							v-if="requestsLoading"
							class="flex items-center gap-1 justify-start sm:justify-end mt-1"
						>
							<div class="flex space-x-1">
								<div
									class="w-1 h-1 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"
								></div>
								<div
									class="w-1 h-1 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"
								></div>
								<div
									class="w-1 h-1 bg-blue-500 rounded-full animate-bounce"
								></div>
							</div>
							<span class="text-xs text-gray-500">Calculando...</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Ver detalle completo -->
			<button
				class="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
				@click="
					router.push({
						name: 'trips.show',
						params: { cef: cef.id },
						query: {
							tab: activeTab ?? 'current',
						},
					})
				"
			>
				<span class="flex items-center justify-center gap-2">
					<svg
						class="h-4 w-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
						/>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
						/>
					</svg>
					Ver detalle completo
				</span>
			</button>

			<!-- Lista de servicios -->
			<div class="space-y-3">
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-2">
						<div class="p-1.5 bg-indigo-100 rounded-lg">
							<svg
								class="w-4 h-4 text-indigo-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
								></path>
							</svg>
						</div>
						<h3 class="font-semibold text-gray-900">Servicios Incluidos</h3>
					</div>
					<span
						class="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium"
					>
						{{ serviceCount }}
						{{ serviceCount === 1 ? 'servicio' : 'servicios' }}
					</span>
				</div>

				<div class="space-y-2">
					<template v-if="requestsLoading">
						<template
							v-for="service in cef.usedServices"
							:key="service.name"
						>
							<div
								v-for="provider in service.providers"
								:key="provider.confirmation"
								class="relative bg-gray-50 rounded-lg p-3 border border-gray-200"
							>
								<div class="space-y-3">
									<!-- Header — mismo layout que ListRequest -->
									<div class="flex justify-between items-start">
										<div class="flex-1 space-y-2">
											<div class="flex items-center gap-2">
												<div class="p-1 bg-blue-100 rounded-lg">
													<span class="material-symbols-outlined text-blue-600 text-sm">
														{{ serviceIcon(service.name) }}
													</span>
												</div>
												<h3 class="font-semibold text-gray-900 text-base capitalize">
													{{ provider.suppliableName }}
												</h3>
											</div>
											<div class="flex items-center gap-2 pl-7">
												<span class="text-sm text-gray-600 font-medium">
													No. Confirmación: #{{ provider.confirmation }}
												</span>
											</div>
										</div>
										<div class="h-8 w-28 bg-gray-200 rounded-lg animate-pulse"></div>
									</div>

									<!-- Fechas + skeleton de status -->
									<div class="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
										<div class="flex items-center gap-2">
											<svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											</svg>
											<span class="text-sm text-gray-600 font-medium capitalize">
												{{ useDateFormat(provider.startDate).dateNoDay }} – {{ useDateFormat(provider.endDate).dateNoDay }}
											</span>
										</div>
										<div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
									</div>

									<!-- Skeleton de tarifa -->
									<div class="border border-gray-200 rounded-lg overflow-hidden">
										<div class="p-3 bg-white flex justify-between items-center">
											<div class="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
											<div class="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
										</div>
									</div>
								</div>
							</div>
						</template>
					</template>

					<template v-else>
						<div
							v-for="request in requests"
							:key="request.id"
							class="relative bg-gray-50 rounded-lg p-3 border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
						>
							<ListRequest
								:request="request"
								@refetch="refetch"
							/>
						</div>
					</template>

					<div
						v-if="!requestsLoading && requests.length === 0"
						class="bg-gray-50 rounded-lg p-6 text-center border border-gray-200"
					>
						<div class="space-y-3">
							<div
								class="mx-auto w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-6 h-6 text-gray-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
									></path>
								</svg>
							</div>
							<div>
								<h4 class="font-medium text-gray-700 mb-1">
									No hay servicios disponibles
								</h4>
								<p class="text-gray-500 text-sm">
									Los servicios aparecerán aquí una vez configurados.
								</p>
							</div>
							<span
								class="inline-flex items-center px-3 py-1 bg-gray-200 rounded text-xs font-medium text-gray-600"
							>
								En espera de servicios
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
